'use client';

import { useRef, useCallback } from 'react';
import { isInputSafe } from '@/app/utils/sanitization';

// Lazy-loaded zipcodes module for bundle size optimization
type ZipcodesModule = typeof import('zipcodes');

export type PostalCodeCountry = 'US' | 'CA';

interface PostalCodeConfig {
  regex: RegExp;
  maxLength: number;
  placeholder: string;
  formatError: string;
  invalidError: string;
}

const CONFIG: Record<PostalCodeCountry, PostalCodeConfig> = {
  US: {
    regex: /^\d{5}$/,
    maxLength: 5,
    placeholder: '12345',
    formatError: 'Please enter a valid 5-digit zip code',
    invalidError: 'Invalid zip code',
  },
  CA: {
    regex: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
    maxLength: 7,
    placeholder: 'A1A 1A1',
    formatError: 'Please enter a valid postal code',
    invalidError: 'Invalid postal code',
  },
};

/**
 * Hook for validating and formatting postal codes (US zip codes or Canadian postal codes)
 */
export const usePostalCodeValidation = (country: PostalCodeCountry = 'US') => {
  const zipcodesRef = useRef<ZipcodesModule | null>(null);
  const config = CONFIG[country];

  /**
   * Formats the input value as user types
   */
  const formatInput = useCallback((value: string): string => {
    if (country === 'US') {
      return value.replace(/\D/g, '').slice(0, config.maxLength);
    }
    
    // Canadian postal code: A1A 1A1 format
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
    if (cleaned.length <= 3) {
      return cleaned;
    }
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  }, [country, config.maxLength]);

  /**
   * Validates the postal code asynchronously (includes real lookup)
   */
  const validate = useCallback(async (input: string): Promise<string | null> => {
    if (!input.trim()) {
      return 'This field is required';
    }

    // Check format
    if (!config.regex.test(input)) {
      return config.formatError;
    }

    // Dynamically load zipcodes package only when needed
    if (!zipcodesRef.current) {
      zipcodesRef.current = await import('zipcodes');
    }

    // Validate it's a real postal code
    const lookupValue = country === 'CA' ? input.replace(/\s/g, '') : input;
    const postalData = zipcodesRef.current.lookup(lookupValue);
    if (!postalData) {
      return config.invalidError;
    }

    // Check for potentially malicious patterns
    if (!isInputSafe(input)) {
      return 'Input contains invalid characters';
    }

    return null;
  }, [country, config]);

  return {
    validate,
    formatInput,
    maxLength: config.maxLength,
    placeholder: config.placeholder,
    regex: config.regex,
  };
};
